<template>
  <div :dir="props.dir" class="v-select" :class="stateClasses">
    <slot name="header" v-bind="scope.header" />
    <div
      :id="`vs${uid}__combobox`"
      class="vs__dropdown-toggle"
      role="combobox"
      :aria-expanded="dropdownOpen.toString()"
      :aria-owns="`vs${uid}__listbox`"
      aria-label="Search for option"
      @mousedown="toggleDropdown($event)"
    >
      <div ref="selectedOptions" class="vs__selected-options">
        <template v-for="option in selectedValue" :key="getOptionKey(option)">
          <slot
            name="selected-option-container"
            :option="normalizeOptionForSlot(option)"
            :deselect="deselect"
            :multiple="props.multiple"
            :disabled="props.disabled"
          >
            <span class="vs__selected">
              <slot
                name="selected-option"
                v-bind="normalizeOptionForSlot(option)"
              >
                {{ getOptionLabel(option) }}
              </slot>
              <button
                v-if="props.multiple"
                ref="deselectButtons"
                :disabled="props.disabled"
                type="button"
                class="vs__deselect"
                :title="`Deselect ${getOptionLabel(option)}`"
                :aria-label="`Deselect ${getOptionLabel(option)}`"
                @click="deselect(option)"
              >
                <span class="vs__icon-indicator-remove" />
              </button>
            </span>
          </slot>
        </template>

        <slot name="search" v-bind="scope.search">
          <input
            ref="search"
            class="vs__search"
            v-bind="scope.search.attributes"
            v-on="scope.search.events"
          />
        </slot>
      </div>

      <div ref="actions" class="vs__actions">
        <button
          v-show="showClearButton"
          ref="clearButton"
          :disabled="props.disabled"
          type="button"
          class="vs__clear"
          title="Clear Selected"
          aria-label="Clear Selected"
          @click="clearSelection"
        >
          <span class="vs__icon-indicator-remove" />
        </button>

        <slot name="open-indicator" v-bind="scope.openIndicator">
          <span
            v-if="!props.noDrop"
            role="presentation"
            class="vs__icon-indicator-open vs__open-indicator"
          />
        </slot>

        <slot name="spinner" v-bind="scope.spinner">
          <div v-show="mutableLoading" class="vs__spinner">Loading...</div>
        </slot>
      </div>
    </div>
    <transition :name="props.transition">
      <ul
        v-if="dropdownOpen"
        :id="`vs${uid}__listbox`"
        ref="dropdownMenu"
        :key="`vs${uid}__listbox`"
        class="vs__dropdown-menu"
        role="listbox"
        tabindex="-1"
        @mousedown.prevent="onMousedown"
        @mouseup="onMouseUp"
      >
        <slot name="list-header" v-bind="scope.listHeader" />
        <li
          v-for="(option, index) in filteredOptions"
          :id="`vs${uid}__option-${index}`"
          :key="getOptionKey(option)"
          role="option"
          class="vs__dropdown-option"
          :class="{
            'vs__dropdown-option--selected': isOptionSelected(option),
            'vs__dropdown-option--highlight': index === typeAheadPointer,
            'vs__dropdown-option--disabled': !props.selectable(option),
          }"
          :aria-selected="index === typeAheadPointer ? true : null"
          @mouseover="
            props.selectable(option) ? (typeAheadPointer = index) : null
          "
          @mousedown.prevent.stop="
            props.selectable(option) ? select(option) : null
          "
        >
          <slot name="option" v-bind="normalizeOptionForSlot(option)">
            {{ getOptionLabel(option) }}
          </slot>
        </li>
        <li v-if="filteredOptions.length === 0" class="vs__no-options">
          <slot name="no-options" v-bind="scope.noOptions">
            Sorry, no matching options.
          </slot>
        </li>
        <slot name="list-footer" v-bind="scope.listFooter" />
      </ul>
      <ul
        v-else
        :id="`vs${uid}__listbox`"
        role="listbox"
        style="display: none; visibility: hidden"
      ></ul>
    </transition>
    <slot name="footer" v-bind="scope.footer" />
  </div>
</template>

<script setup>
import { computed, ref, useId, useSlots, useTemplateRef, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: null,
    default: undefined,
  },
  options: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  transition: {
    type: String,
    default: 'vs__fade',
  },
  clearSearchOnSelect: {
    type: Boolean,
    default: true,
  },
  closeOnSelect: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: 'label',
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  reduce: {
    type: Function,
    default: undefined,
  },
  selectable: {
    type: Function,
    default: () => true,
  },
  getOptionLabel: {
    type: Function,
    default: undefined,
  },
  getOptionKey: {
    type: Function,
    default: undefined,
  },
  onTab: {
    type: Function,
    default: undefined,
  },
  taggable: {
    type: Boolean,
    default: false,
  },
  tabindex: {
    type: Number,
    default: null,
  },
  pushTags: {
    type: Boolean,
    default: false,
  },
  filterable: {
    type: Boolean,
    default: true,
  },
  filterBy: {
    type: Function,
    default: (option, label, search) =>
      (label || '').toLowerCase().indexOf(search.toLowerCase()) > -1,
  },
  filter: {
    type: Function,
    default: undefined,
  },
  createOption: {
    type: Function,
    default: undefined,
  },
  resetOnOptionsChange: {
    type: [Boolean, Function],
    default: false,
  },
  clearSearchOnBlur: {
    type: Function,
    default: ({ clearSearchOnSelect, multiple }) =>
      clearSearchOnSelect && !multiple,
  },
  noDrop: {
    type: Boolean,
    default: false,
  },
  inputId: {
    type: String,
    default: undefined,
  },
  dir: {
    type: String,
    default: 'auto',
  },
  selectOnTab: {
    type: Boolean,
    default: false,
  },
  selectOnKeyCodes: {
    type: Array,
    default: () => [13],
  },
  searchInputQuerySelector: {
    type: String,
    default: '[type=search]',
  },
  mapKeydown: {
    type: Function,
    default: (map) => map,
  },
  dropdownShouldOpen: {
    type: Function,
    default: ({ noDrop, open, mutableLoading }) =>
      noDrop ? false : open && !mutableLoading,
  },
  autoscroll: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:modelValue',
  'open',
  'close',
  'search',
  'search:blur',
  'search:focus',
  'option:selecting',
  'option:selected',
  'option:deselecting',
  'option:deselected',
  'option:created',
])

const slots = useSlots()

const selectedOptionsRef = useTemplateRef('selectedOptions')
const searchRef = useTemplateRef('search')
const clearButtonRef = useTemplateRef('clearButton')
const dropdownMenuRef = useTemplateRef('dropdownMenu')
const deselectButtonsRef = useTemplateRef('deselectButtons')

const uid = useId()
const search = ref('')
const open = ref(false)
const isComposing = ref(false)
const pushedTags = ref([])
const internalValue = ref([])
const mutableLoading = ref(props.loading)
const typeAheadPointer = ref(-1)

let mousedown = false

const reduceOption = (option) => (props.reduce ? props.reduce(option) : option)

const isTrackingValues = computed(
  () => typeof props.modelValue === 'undefined' || !!props.reduce,
)

const getOptionLabel = (option) => {
  if (props.getOptionLabel) {
    return props.getOptionLabel(option)
  }

  if (typeof option === 'object') {
    if (!Object.prototype.hasOwnProperty.call(option, props.label)) {
      return console.warn(
        `[vue-select warn]: Label key "option.${props.label}" does not` +
          ` exist in options object ${JSON.stringify(option)}.\n` +
          'https://vue-select.org/api/props.html#getoptionlabel',
      )
    }
    return option[props.label]
  }

  return option
}

const sortAndStringify = (sortable) => {
  const ordered = {}

  Object.keys(sortable)
    .sort()
    .forEach((key) => {
      ordered[key] = sortable[key]
    })

  return JSON.stringify(ordered)
}

const getOptionKey = (option) => {
  if (props.getOptionKey) {
    return props.getOptionKey(option)
  }

  if (typeof option !== 'object') {
    return option
  }

  try {
    return Object.prototype.hasOwnProperty.call(option, 'id')
      ? option.id
      : sortAndStringify(option)
  } catch (e) {
    const warning =
      `[vue-select warn]: Could not stringify this option ` +
      `to generate unique key. Please provide'getOptionKey' prop ` +
      `to return a unique key for each option.\n` +
      'https://vue-select.org/api/props.html#getoptionkey'
    return console.warn(warning, option, e)
  }
}

const optionComparator = (a, b) => getOptionKey(a) === getOptionKey(b)

const selectedValue = computed(() => {
  const value = isTrackingValues.value ? internalValue.value : props.modelValue

  if (value) {
    return [].concat(value)
  }

  return []
})

const optionList = computed(() =>
  props.options.concat(props.pushTags ? pushedTags.value : []),
)

const searching = computed(() => !!search.value)

const isValueEmpty = computed(() => selectedValue.value.length === 0)

const searchPlaceholder = computed(() => {
  if (isValueEmpty.value && props.placeholder) {
    return props.placeholder
  }
  return undefined
})

const optionExists = (option) =>
  optionList.value.some((_option) => optionComparator(_option, option))

const createOption = (option) => {
  if (props.createOption) {
    return props.createOption(option)
  }

  return typeof optionList.value[0] === 'object'
    ? { [props.label]: option }
    : option
}

const instance = () => ({
  noDrop: props.noDrop,
  open: open.value,
  mutableLoading: mutableLoading.value,
  search: search.value,
  multiple: props.multiple,
  clearSearchOnSelect: props.clearSearchOnSelect,
  selectedValue: selectedValue.value,
})

const filterOptions = (options, query) => {
  if (props.filter) {
    return props.filter(options, query, instance())
  }

  return options.filter((option) => {
    let label = getOptionLabel(option)
    if (typeof label === 'number') {
      label = label.toString()
    }
    return props.filterBy(option, label, query)
  })
}

const filteredOptions = computed(() => {
  const list = [].concat(optionList.value)

  if (!props.filterable && !props.taggable) {
    return list
  }

  const options = search.value.length ? filterOptions(list, search.value) : list
  if (props.taggable && search.value.length) {
    const createdOption = createOption(search.value)
    if (!optionExists(createdOption)) {
      options.unshift(createdOption)
    }
  }
  return options
})

const dropdownOpen = computed(() => props.dropdownShouldOpen(instance()))

const showClearButton = computed(
  () =>
    !props.multiple && props.clearable && !open.value && !isValueEmpty.value,
)

const stateClasses = computed(() => ({
  'vs--open': dropdownOpen.value,
  'vs--single': !props.multiple,
  'vs--searching': searching.value && !props.noDrop,
  'vs--searchable': props.searchable && !props.noDrop,
  'vs--unsearchable': !props.searchable,
  'vs--loading': mutableLoading.value,
  'vs--disabled': props.disabled,
}))

const getSearchEl = () =>
  slots.search
    ? selectedOptionsRef.value?.querySelector(props.searchInputQuerySelector)
    : searchRef.value

const findOptionFromReducedValue = (value) => {
  const predicate = (option) =>
    JSON.stringify(reduceOption(option)) === JSON.stringify(value)

  const matches = [...props.options, ...pushedTags.value].filter(predicate)

  if (matches.length === 1) {
    return matches[0]
  }

  return (
    matches.find((match) => optionComparator(match, internalValue.value)) ||
    value
  )
}

const setInternalValueFromOptions = (value) => {
  if (Array.isArray(value)) {
    internalValue.value = value.map((val) => findOptionFromReducedValue(val))
  } else {
    internalValue.value = findOptionFromReducedValue(value)
  }
}

const updateValue = (value) => {
  if (typeof props.modelValue === 'undefined') {
    internalValue.value = value
  }

  if (value !== null) {
    if (Array.isArray(value)) {
      value = value.map((val) => reduceOption(val))
    } else {
      value = reduceOption(value)
    }
  }

  emit('update:modelValue', value)
}

const isOptionSelected = (option) =>
  selectedValue.value.some((value) => optionComparator(value, option))

const pushTag = (option) => {
  pushedTags.value.push(option)
}

const onAfterSelect = () => {
  if (props.closeOnSelect) {
    open.value = !open.value
    getSearchEl()?.blur()
  }

  if (props.clearSearchOnSelect) {
    search.value = ''
  }
}

const select = (option) => {
  emit('option:selecting', option)
  if (!isOptionSelected(option)) {
    if (props.taggable && !optionExists(option)) {
      emit('option:created', option)
      pushTag(option)
    }
    if (props.multiple) {
      option = selectedValue.value.concat(option)
    }
    updateValue(option)
    emit('option:selected', option)
  }
  onAfterSelect(option)
}

const deselect = (option) => {
  emit('option:deselecting', option)
  updateValue(
    selectedValue.value.filter((val) => !optionComparator(val, option)),
  )
  emit('option:deselected', option)
}

const clearSelection = () => {
  updateValue(props.multiple ? [] : null)
}

const toggleDropdown = (event) => {
  const searchEl = getSearchEl()
  const targetIsNotSearch = event.target !== searchEl
  if (targetIsNotSearch) {
    event.preventDefault()
  }

  const ignoredButtons = [
    ...(deselectButtonsRef.value || []),
    clearButtonRef.value,
  ]

  if (
    !searchEl ||
    ignoredButtons
      .filter(Boolean)
      .some((el) => el.contains(event.target) || el === event.target)
  ) {
    event.preventDefault()
    return
  }

  if (open.value && targetIsNotSearch) {
    searchEl.blur()
  } else if (!props.disabled) {
    open.value = true
    searchEl.focus()
  }
}

const closeSearchOptions = () => {
  open.value = false
  emit('search:blur')
}

const maybeDeleteValue = () => {
  const searchEl = getSearchEl()
  if (
    !searchEl?.value.length &&
    selectedValue.value &&
    selectedValue.value.length &&
    props.clearable
  ) {
    let value = null
    if (props.multiple) {
      value = [...selectedValue.value.slice(0, selectedValue.value.length - 1)]
    }
    updateValue(value)
  }
}

const normalizeOptionForSlot = (option) =>
  typeof option === 'object' ? option : { [props.label]: option }

const onEscape = () => {
  if (!search.value.length) {
    getSearchEl()?.blur()
  } else {
    search.value = ''
  }
}

const onSearchBlur = () => {
  if (mousedown && !searching.value) {
    mousedown = false
  } else {
    const { clearSearchOnSelect, multiple } = props
    if (props.clearSearchOnBlur({ clearSearchOnSelect, multiple })) {
      search.value = ''
    }
    closeSearchOptions()
    return
  }
  if (search.value.length === 0 && props.options.length === 0) {
    closeSearchOptions()
  }
}

const onSearchFocus = () => {
  open.value = true
  emit('search:focus')
}

const onMousedown = () => {
  mousedown = true
}

const onMouseUp = () => {
  mousedown = false
}

const typeAheadUp = () => {
  for (let i = typeAheadPointer.value - 1; i >= 0; i--) {
    if (props.selectable(filteredOptions.value[i])) {
      typeAheadPointer.value = i
      break
    }
  }
}

const typeAheadDown = () => {
  for (
    let i = typeAheadPointer.value + 1;
    i < filteredOptions.value.length;
    i++
  ) {
    if (props.selectable(filteredOptions.value[i])) {
      typeAheadPointer.value = i
      break
    }
  }
}

const typeAheadSelect = () => {
  const typeAheadOption = filteredOptions.value[typeAheadPointer.value]

  if (typeAheadOption) {
    select(typeAheadOption)
  }
}

const onTab = () => {
  if (props.onTab) {
    return props.onTab()
  }

  if (props.selectOnTab && !isComposing.value) {
    typeAheadSelect()
  }
}

const onSearchKeyDown = (e) => {
  const preventAndSelect = (event) => {
    event.preventDefault()
    return !isComposing.value && typeAheadSelect()
  }

  const defaults = {
    8: () => maybeDeleteValue(),
    9: () => onTab(),
    27: () => onEscape(),
    38: (event) => {
      event.preventDefault()
      return typeAheadUp()
    },
    40: (event) => {
      event.preventDefault()
      return typeAheadDown()
    },
  }

  props.selectOnKeyCodes.forEach((keyCode) => {
    defaults[keyCode] = preventAndSelect
  })

  const handlers = props.mapKeydown(defaults, instance())

  if (typeof handlers[e.keyCode] === 'function') {
    return handlers[e.keyCode](e)
  }
}

const toggleLoading = (toggle = null) => {
  if (toggle == null) {
    return (mutableLoading.value = !mutableLoading.value)
  }
  return (mutableLoading.value = toggle)
}

const getDropdownViewport = () =>
  dropdownMenuRef.value
    ? dropdownMenuRef.value.getBoundingClientRect()
    : { height: 0, top: 0, bottom: 0 }

const maybeAdjustScroll = () => {
  const optionEl =
    dropdownMenuRef.value?.children[typeAheadPointer.value] || false

  if (optionEl) {
    const bounds = getDropdownViewport()
    const { top, bottom, height } = optionEl.getBoundingClientRect()

    if (top < bounds.top) {
      return (dropdownMenuRef.value.scrollTop = optionEl.offsetTop)
    } else if (bottom > bounds.bottom) {
      return (dropdownMenuRef.value.scrollTop =
        optionEl.offsetTop - (bounds.height - height))
    }
  }
}

const scope = computed(() => {
  const listSlot = {
    search: search.value,
    loading: props.loading,
    searching: searching.value,
    filteredOptions: filteredOptions.value,
  }

  return {
    search: {
      attributes: {
        disabled: props.disabled,
        placeholder: searchPlaceholder.value,
        tabindex: props.tabindex,
        readonly: !props.searchable,
        id: props.inputId,
        'aria-autocomplete': 'list',
        'aria-labelledby': `vs${uid}__combobox`,
        'aria-controls': `vs${uid}__listbox`,
        type: 'search',
        autocomplete: props.autocomplete,
        value: search.value,
        ...(dropdownOpen.value && filteredOptions.value[typeAheadPointer.value]
          ? {
              'aria-activedescendant': `vs${uid}__option-${typeAheadPointer.value}`,
            }
          : {}),
      },
      events: {
        compositionstart: () => (isComposing.value = true),
        compositionend: () => (isComposing.value = false),
        keydown: onSearchKeyDown,
        blur: onSearchBlur,
        focus: onSearchFocus,
        input: (e) => (search.value = e.target.value),
      },
    },
    spinner: {
      loading: mutableLoading.value,
    },
    noOptions: {
      search: search.value,
      loading: mutableLoading.value,
      searching: searching.value,
    },
    openIndicator: {
      attributes: {
        role: 'presentation',
        class: 'vs__open-indicator',
      },
    },
    listHeader: listSlot,
    listFooter: listSlot,
    header: { ...listSlot, deselect },
    footer: { ...listSlot, deselect },
  }
})

watch(
  () => props.options,
  (newOptions, oldOptions) => {
    const shouldReset = () =>
      typeof props.resetOnOptionsChange === 'function'
        ? props.resetOnOptionsChange(
            newOptions,
            oldOptions,
            selectedValue.value,
          )
        : props.resetOnOptionsChange

    if (!props.taggable && shouldReset()) {
      clearSelection()
    }

    if (props.modelValue && isTrackingValues.value) {
      setInternalValueFromOptions(props.modelValue)
    }
  },
  { deep: 1 },
)

watch(
  () => props.modelValue,
  (val) => {
    if (isTrackingValues.value) {
      setInternalValueFromOptions(val)
    }
  },
  { deep: 1 },
)

watch(
  () => props.multiple,
  () => clearSelection(),
)

watch(open, (isOpen) => {
  emit(isOpen ? 'open' : 'close')
})

watch(search, () => {
  emit('search', search.value, toggleLoading)
})

watch(
  () => props.loading,
  (val) => {
    mutableLoading.value = val
  },
)

watch(filteredOptions, () => {
  for (let i = 0; i < filteredOptions.value.length; i++) {
    if (props.selectable(filteredOptions.value[i])) {
      typeAheadPointer.value = i
      break
    }
  }
})

watch(typeAheadPointer, () => {
  if (props.autoscroll) {
    maybeAdjustScroll()
  }
})

if (typeof props.modelValue !== 'undefined' && isTrackingValues.value) {
  setInternalValueFromOptions(props.modelValue)
}

defineExpose({ select, deselect, clearSelection, toggleLoading })
</script>
